import { useCallback, useState } from "react";
import {
  EnhancedTableColumn,
  EnhancedTableRow,
  EnhancedTableState,
  TableSortDir
} from "../types/enhanced-table";
import { debounce } from 'throttle-debounce';

interface TableStateProps {
  defaultSort?: string;
  pageSize?: number;
}

export const useTableState = (props: TableStateProps = {}) : EnhancedTableState => {
  const {defaultSort, pageSize = 10} = props;

  const [query, setQuery] = useState("");
  const [sortDir, setSortDir] = useState<TableSortDir>('asc');
  const [sortBy, setSortBy] = useState<string>(defaultSort);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  const reset = () => {
    setQuery("");
    setSortDir("asc");
    setSortBy(defaultSort);
    setPageNumber(0);
    setRowsPerPage(pageSize);
  };

  const descendingComparator = (a, b, sortBy) => {
    let compA = a[sortBy];
    let compB = b[sortBy];

    if (typeof compA === "string") {
      compA = compA.toLowerCase();
    }

    if (typeof compB === "string") {
      compB = compB.toLowerCase();
    }

    if (compB < compA) {
      return -1;
    } else if (compB > compA) {
      return 1;
    } else {
      return 0;
    }
  };

  const getComparator = (sortDir, sortBy) => {
    return sortDir === 'desc'
      ? (a, b) => descendingComparator(a, b, sortBy)
      : (a, b) => -descendingComparator(a, b, sortBy);
  };

  const handlePageNumberChange = (event, value) => {
    setPageNumber(value);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPageNumber(0);
  };

  const handleSearchChange = useCallback(debounce(200, (value) => {
    setQuery(value);
    setPageNumber(0);
  }), []);

  const handleRequestSort = (event, value) => {
    const ascending = sortBy === value && sortDir === 'asc';
    setSortDir(ascending ? 'desc' : 'asc');
    setSortBy(value);
  };

  const handleSearch = (columns: EnhancedTableColumn[], rows: EnhancedTableRow[]) => {
    if (!query) return rows;

    const queryParts = query
      .split(" ")
      .map(x => x.toLowerCase());

    const searchableColumns = columns
      .filter(column => column.searchable)
      .map(column => column.id);

    return rows.filter(row => {
      let valid = false;

      searchableColumns.forEach(column => {
        if (valid) return;

        const parts = row[column]
          .split(" ")
          .map(x => x.toLowerCase());

        valid = valid || queryParts.filter(text => {
          return parts.filter(x => x.includes(text)).length > 0;
        }).length === queryParts.length;
      });

      return valid;
    });
  };

  const handleSort = (columns: EnhancedTableColumn[], rows: EnhancedTableRow[]) => {
    const column = columns.find(column => column.id === sortBy);
    if (!column) return rows;

    const comparator = column.sortBy
      ? getComparator(sortDir, column.sortBy)
      : getComparator(sortDir, sortBy);

    rows.sort(comparator);
    return rows;
  };

  const handlePages = (rows: EnhancedTableRow[]) => {
    return rows.slice(
      pageNumber * rowsPerPage,
      pageNumber * rowsPerPage + rowsPerPage
    );
  };

  return {
    query,
    rowsPerPage,
    pageNumber,
    sortDir,
    sortBy,
    handlePageNumberChange,
    handleRowsPerPageChange,
    handleSearchChange,
    handleRequestSort,
    handleSearch,
    handleSort,
    handlePages,
    reset
  };
};